import { supabase } from "@/lib/supabaseClient";

export interface ReadingQuizOption {
  question: string;
  options: string[];
  correct_answer_index: number;
  explanation: string;
}

export interface ReadingArticle {
  id: string;
  title: string;
  slug: string;
  category: string;
  level: string;
  estimated_time: string;
  content: string;
  quiz?: ReadingQuizOption[];
  created_at?: string;
  updated_at?: string;
}

export interface ReadingProgress {
  id?: string;
  user_id?: string;
  article_id: string;
  status: "not_started" | "in_progress" | "completed";
  score: number;
  answers?: any;
  completed_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

class ReadingService {
  /**
   * Fetch all reading articles.
   */
  async getArticles(): Promise<ReadingArticle[]> {
    if (!supabase) return [];
    try {
      const { data, error } = await supabase
        .from("reading_articles")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("[ReadingService] Error fetching articles:", error);
        return [];
      }
      return data || [];
    } catch (err) {
      console.error("[ReadingService] Exception fetching articles:", err);
      return [];
    }
  }

  /**
   * Fetch a single article by its slug.
   */
  async getArticleBySlug(slug: string): Promise<ReadingArticle | null> {
    if (!supabase) return null;
    try {
      const { data, error } = await supabase
        .from("reading_articles")
        .select("*")
        .eq("slug", slug)
        .single();

      if (error) {
        console.error(`[ReadingService] Error fetching article ${slug}:`, error);
        return null;
      }
      return data;
    } catch (err) {
      console.error(`[ReadingService] Exception fetching article ${slug}:`, err);
      return null;
    }
  }

  /**
   * Import a single article from JSON payload (admin).
   */
  async importArticle(payload: Omit<ReadingArticle, "id" | "created_at" | "updated_at">): Promise<{ success: boolean; error?: string }> {
    if (!supabase) return { success: false, error: "Supabase client not initialized" };
    try {
      const { error } = await supabase
        .from("reading_articles")
        .upsert(payload, { onConflict: "slug" });

      if (error) {
        console.error("[ReadingService] Error importing article:", error);
        return { success: false, error: error.message };
      }
      return { success: true };
    } catch (err: any) {
      console.error("[ReadingService] Exception importing article:", err);
      return { success: false, error: err.message || String(err) };
    }
  }

  /**
   * Update a single article by its ID.
   */
  async updateArticle(id: string, payload: Partial<ReadingArticle>): Promise<{ success: boolean; error?: string }> {
    if (!supabase) return { success: false, error: "Supabase client not initialized" };
    try {
      // Remove id from payload if it exists to prevent updating primary key
      const { id: _, ...updateData } = payload as any;
      updateData.updated_at = new Date().toISOString();

      const { error } = await supabase
        .from("reading_articles")
        .update(updateData)
        .eq("id", id);

      if (error) {
        console.error("[ReadingService] Error updating article:", error);
        return { success: false, error: error.message };
      }
      return { success: true };
    } catch (err: any) {
      console.error("[ReadingService] Exception updating article:", err);
      return { success: false, error: err.message || String(err) };
    }
  }

  /**
   * Delete a single article by its ID.
   */
  async deleteArticle(id: string): Promise<{ success: boolean; error?: string }> {
    if (!supabase) return { success: false, error: "Supabase client not initialized" };
    try {
      const { error } = await supabase
        .from("reading_articles")
        .delete()
        .eq("id", id);

      if (error) {
        console.error("[ReadingService] Error deleting article:", error);
        return { success: false, error: error.message };
      }
      return { success: true };
    } catch (err: any) {
      console.error("[ReadingService] Exception deleting article:", err);
      return { success: false, error: err.message || String(err) };
    }
  }

  /**
   * Fetch all reading progress records for the currently authenticated user.
   */
  async fetchUserProgress(): Promise<ReadingProgress[]> {
    if (!supabase) return [];
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) return [];

      const { data, error } = await supabase
        .from("reading_progress")
        .select("*")
        .eq("user_id", userData.user.id);

      if (error) {
        console.error("[ReadingService] Error fetching reading progress:", error);
        return [];
      }
      return data || [];
    } catch (err) {
      console.error("[ReadingService] Exception fetching reading progress:", err);
      return [];
    }
  }

  /**
   * Save / Upsert user progress for a specific article.
   */
  async updateReadingProgress(
    articleId: string,
    status: "not_started" | "in_progress" | "completed",
    score: number,
    answers?: any
  ): Promise<ReadingProgress | null> {
    if (!supabase) return null;
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) {
        console.warn("[ReadingService] Unauthenticated progress update attempt.");
        return null;
      }

      // Check for existing progress first
      const { data: existingProgress, error: fetchError } = await supabase
        .from("reading_progress")
        .select("id")
        .eq("user_id", userData.user.id)
        .eq("article_id", articleId)
        .maybeSingle();

      if (fetchError) {
        console.error("[ReadingService] Error fetching existing progress:", fetchError);
        return null;
      }

      const payload: any = {
        user_id: userData.user.id,
        article_id: articleId,
        status,
        score,
        completed_at: status === "completed" ? new Date().toISOString() : null,
        updated_at: new Date().toISOString()
      };

      if (answers !== undefined) {
        payload.answers = answers;
      }

      let queryResult;
      if (existingProgress?.id) {
        queryResult = await supabase
          .from("reading_progress")
          .update(payload)
          .eq("id", existingProgress.id)
          .select()
          .single();
      } else {
        queryResult = await supabase
          .from("reading_progress")
          .insert(payload)
          .select()
          .single();
      }

      if (queryResult.error) {
        console.error("[ReadingService] Error updating progress:", queryResult.error);
        return null;
      }
      return queryResult.data;
    } catch (err) {
      console.error("[ReadingService] Exception updating progress:", err);
      return null;
    }
  }
}

export const readingService = new ReadingService();
